/**
 * The HTTP seam — API_CONTRACT.md §6, §8.
 *
 * Everything the backend guarantees is honoured here once, so no call site has
 * to remember it:
 *
 *   · problem+json becomes a typed `ApiError` carrying the machine-readable code
 *   · a 401 refreshes ONCE and replays; a failed refresh logs out
 *   · retry happens only when the RESPONSE says `retryable` — never on a 409,
 *     which by §17.5 means the work was already done or the state moved
 *   · `Idempotency-Key` is attached to every ⟳ command
 *   · `If-Match` carries the optimistic-concurrency token
 */
import { ApiError } from './errors.js';
import { apiRuntime } from './runtime.js';
import { accessTokenExpired, clearTokens, getAccessToken, getRefreshToken, setTokens, } from './auth.js';
function buildUrl(path, query) {
    const url = `${apiRuntime().baseUrl}${path}`;
    if (!query)
        return url;
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
        if (value !== undefined)
            params.set(key, String(value));
    }
    const qs = params.toString();
    return qs ? `${url}?${qs}` : url;
}
/** A single in-flight refresh, shared by every 401 that arrives while it runs. */
let refreshInFlight = null;
/**
 * Trade the refresh token for a new pair NOW, even though the access token is
 * still valid. Needed when the server has just changed what this account may
 * do: a driver who registers is granted `cl_driver`, and the token in hand was
 * minted without it.
 */
export async function renewSession() {
    return refreshSession();
}
async function refreshSession() {
    if (refreshInFlight)
        return refreshInFlight;
    const refreshToken = getRefreshToken();
    if (!refreshToken)
        return false;
    refreshInFlight = (async () => {
        try {
            const response = await fetch(buildUrl('/v1/auth/token/refresh', undefined), {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify({ refresh_token: refreshToken }),
            });
            if (!response.ok)
                return false;
            const body = (await response.json());
            setTokens({
                accessToken: body.access_token,
                refreshToken: body.refresh_token,
                expiresIn: body.expires_in,
            });
            return true;
        }
        catch {
            return false;
        }
        finally {
            refreshInFlight = null;
        }
    })();
    return refreshInFlight;
}
async function toApiError(response) {
    let problem = {};
    try {
        problem = (await response.json());
    }
    catch {
        problem = { title: response.statusText };
    }
    return new ApiError(response.status, problem);
}
async function send(path, options, retried) {
    const headers = { accept: 'application/json' };
    if (options.body !== undefined)
        headers['content-type'] = 'application/json';
    if (options.idempotencyKey)
        headers['idempotency-key'] = options.idempotencyKey;
    if (options.ifMatch)
        headers['if-match'] = options.ifMatch;
    if (!options.skipAuth) {
        // Refresh proactively when the token has aged out, so the common path is
        // one request rather than a 401 round-trip.
        if (accessTokenExpired() && getRefreshToken())
            await refreshSession();
        const token = getAccessToken();
        if (token)
            headers['authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(buildUrl(path, options.query), {
        method: options.method ?? 'GET',
        headers,
        ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
        ...(options.signal ? { signal: options.signal } : {}),
    });
    if (response.status === 401 && !options.skipAuth && !retried) {
        const refreshed = await refreshSession();
        if (refreshed)
            return send(path, options, true);
        clearTokens();
    }
    if (!response.ok)
        throw await toApiError(response);
    const replayed = response.headers.get('idempotency-replayed') === 'true';
    const version = response.headers.get('etag') ?? undefined;
    const data = response.status === 204 ? undefined : (await response.json());
    return { data, replayed, version };
}
export async function request(path, options = {}) {
    return send(path, options, false);
}
/** Convenience for the common case where only the body matters. */
export async function apiGet(path, query) {
    const result = await request(path, query ? { query } : {});
    return result.data;
}
/**
 * React Query's retry predicate. The backend states retryability on the
 * response, so this is derived rather than guessed — and a 409 is NEVER
 * retried, because §17.5 makes it "already done", not "try again".
 */
export function shouldRetry(failureCount, error) {
    if (!(error instanceof ApiError))
        return failureCount < 1;
    if (error.status === 409 || error.status === 404 || error.status === 403)
        return false;
    if (error.status === 422 || error.status === 412)
        return false;
    return error.retryable && failureCount < 2;
}
//# sourceMappingURL=client.js.map